import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  subMonths,
  startOfYear,
  subYears,
  subDays,
  format,
  parseISO,
} from "date-fns";
import { TimeframeOption } from "@constants/types";
import { getVaultChartData } from "@services/index";

type TokenType = "agETH" | "hgETH" | "sbUSD";

interface AprDataPoint {
  apr: string;
  asset: string;
  elapsedDays: number;
  rateIncrease: string;
  startRate: string;
  endRate: string;
}

interface AprApiResponse {
  oneDayAPR?: AprDataPoint;
  sevenDaysAPR?: AprDataPoint;
  oneMonthAPR?: AprDataPoint;
  threeMonthAPR?: AprDataPoint;
  sixMonthsAPR?: AprDataPoint;
  yesterdayAPR?: AprDataPoint;
  YTDAPR?: AprDataPoint;
  allTimeAPR?: AprDataPoint;
  dataSet: Array<{
    sk: string; // date in YYYY-MM-DD format
    rate: string; // exchange rate in wei (18 decimals)
    pk: string;
    updatedAt: string;
  }>;
}

interface ChartDataPoint {
  date: string;
  exchangeRate: number;
  monthlyChange?: number;
}

// Calculate date range based on timeframe using date-fns
const getDateRangeForTimeframe = (
  timeframe: TimeframeOption
): { startDate: Date; endDate: Date } => {
  // Use yesterday's date as end date
  const endDate = subDays(new Date(), 1);
  let startDate: Date;

  switch (timeframe) {
    case "7D":
      startDate = subDays(endDate, 7);
      break;
    case "1M":
      startDate = subMonths(endDate, 1);
      break;
    case "3M":
      startDate = subMonths(endDate, 3);
      break;
    case "6M":
      startDate = subMonths(endDate, 6);
      break;
    case "YTD":
      startDate = startOfYear(endDate);
      break;
    case "ALL":
      // Assuming the vault started 2 years ago
      // You can adjust this to the actual launch date
      startDate = subYears(endDate, 2);
      break;
    default:
      startDate = subDays(endDate, 7);
  }

  return { startDate, endDate };
};

// Process exchange rate data based on timeframe (works for all assets)
const processChartData = (
  apiResponse: AprApiResponse | null,
  timeframe: TimeframeOption
): ChartDataPoint[] => {
  if (!apiResponse || !apiResponse.dataSet) return [];

  const { startDate, endDate } = getDateRangeForTimeframe(timeframe);

  // Convert rate from wei (18 decimals) to decimal
  const convertRate = (rateWei: string): number => {
    if (!rateWei || rateWei === "0") return 0;
    return parseFloat(rateWei) / 1e18;
  };

  // Filter and process data based on timeframe
  const filteredData = apiResponse.dataSet
    .filter((item) => {
      const itemDate = parseISO(item.sk);
      return itemDate >= startDate && itemDate <= endDate;
    })
    .sort((a, b) => parseISO(a.sk).getTime() - parseISO(b.sk).getTime())
    .filter((item) => convertRate(item.rate) > 0); // Filter out zero rates

  // Calculate monthly change for each data point
  return filteredData.map((item, index) => {
    const exchangeRate = convertRate(item.rate);
    let monthlyChange: number | undefined;

    // Calculate monthly change (compare with data point ~30 days ago)
    if (index >= 30) {
      const previousER = convertRate(filteredData[index - 30].rate);
      if (previousER > 0) {
        monthlyChange = ((exchangeRate - previousER) / previousER) * 100;
      }
    }

    return {
      date: item.sk,
      exchangeRate,
      monthlyChange,
    };
  });
};

const useVaultPerformance = (
  tokenType: TokenType,
  timeframe: TimeframeOption = "7D"
) => {
  // Map tokenType to asset name for API
  const assetName = useMemo(() => {
    switch (tokenType) {
      case "agETH":
        return "ageth";
      case "hgETH":
        return "hgeth";
      case "sbUSD":
        return "sbusd";
      default:
        return null;
    }
  }, [tokenType]);

  // Fetch APR data once for any token type (includes full dataset)
  const { data: aprResponse, isLoading: isLoadingApr } = useQuery({
    queryKey: ["apr-data", assetName],
    queryFn: async () => {
      const r = await getVaultChartData(assetName!);
      return r.data;
    },
    enabled: !!assetName,
    staleTime: 1 * 60 * 60 * 1000, // Cache for 1 hour
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Process data based on timeframe (client-side filtering)
  const processedData = useMemo(() => {
    if (aprResponse) {
      return processChartData(aprResponse, timeframe);
    }
    return [];
  }, [aprResponse, timeframe]);

  // Extract APR based on selected timeframe for all token types
  const currentApr = useMemo(() => {
    if (!aprResponse) return null;

    let aprData: AprDataPoint | undefined;
    let label = "";

    switch (timeframe) {
      case "7D":
        // Try to use sevenDaysAPR, fallback to sixMonthsAPR
        aprData = aprResponse.sevenDaysAPR || undefined;
        label = "7 days realised APR post fees";
        break;
      case "1M":
        // Try to use oneMonthAPR, fallback to sixMonthsAPR
        aprData = aprResponse.oneMonthAPR || undefined;
        label = "1 month realised APR post fees";
        break;
      case "3M":
        // Try threeMonthAPR, then sixMonthsAPR as fallback
        aprData = aprResponse.threeMonthAPR || aprResponse.sixMonthsAPR;
        label = "3 months realised APR post fees";
        break;
      case "6M":
        // Use sixMonthsAPR
        aprData = aprResponse.sixMonthsAPR;
        label = "6 months realised APR post fees";
        break;
      case "YTD":
        // Use sixMonthsAPR as best available option for YTD
        aprData = aprResponse.sixMonthsAPR;
        label = "Year-to-date realised APR post fees";
        break;
      case "ALL":
        // Use sixMonthsAPR as best available option for ALL
        aprData = aprResponse.sixMonthsAPR;
        label = "All-time realised APR post fees";
        break;
      default:
        // Default fallback
        aprData =
          aprResponse.sixMonthsAPR ||
          aprResponse.threeMonthAPR ||
          aprResponse.oneMonthAPR ||
          aprResponse.sevenDaysAPR;
        label = aprResponse.sixMonthsAPR
          ? "6 months realised APR post fees"
          : aprResponse.threeMonthAPR
          ? "3 months realised APR post fees"
          : aprResponse.oneMonthAPR
          ? "1 month realised APR post fees"
          : "7 days realised APR post fees";
    }

    if (aprData) {
      const apr = parseFloat(aprData.apr) * 100;
      return { value: apr, label };
    }
    return null;
  }, [aprResponse, timeframe]);

  const chartData = useMemo(() => {
    if (!tokenType) {
      return null;
    }

    // Use new API data with exchange rates for all token types
    if (processedData && processedData.length > 0) {
      return {
        labels: processedData.map((item) =>
          format(parseISO(item.date), "M/d/yy")
        ),
        datasets: [
          {
            label: "Exchange Rate",
            fill: true,
            backgroundColor: "rgba(0, 183, 168, 0.5)",
            borderColor: "rgba(0, 183, 168, 0.5)",
            tension: 0.4,
            borderWidth: 1,
            pointRadius: 0,
            pointHoverRadius: 5,
            data: processedData.map((item) => item.exchangeRate),
          },
        ],
        showExchangeRate: true, // This chart displays exchange rate data
        rawData: processedData, // Pass raw data for custom tooltip
        selectedTimeframeApr: currentApr?.value ?? null, // Pass current APR for display
        aprLabel: currentApr?.label ?? "6 months realised APR post fees",
      };
    }

    // Return empty chart if no data available
    return {
      labels: [],
      datasets: [],
      showExchangeRate: false,
      selectedTimeframeApr: null,
      aprLabel: "6 months realised APR post fees",
    };

    // OLD LOGIC - COMMENTED OUT
    // For agETH and hgETH - multiple datasets with APR breakdown
    // const baseDatasets = [
    //   {
    //     label: "Total APR",
    //     fill: false,
    //     backgroundColor: "#00B7A8",
    //     borderColor: "#4A4A4A",
    //     tension: 0,
    //     borderWidth: 2,
    //     pointRadius: 0,
    //     pointHoverRadius: 5,
    //     order: 1,
    //   },
    //   {
    //     label: "Realised APR",
    //     fill: true,
    //     backgroundColor: "#00B7A8",
    //     borderColor: "#00B7A8",
    //     tension: 0,
    //     borderWidth: 0,
    //     pointRadius: 0,
    //     pointHoverRadius: 5,
    //     order: 2,
    //   },
    //   {
    //     label: "Accruing APR",
    //     fill: true,
    //     backgroundColor: "#66D9CC",
    //     tension: 0,
    //     borderWidth: 0,
    //     pointRadius: 0,
    //     pointHoverRadius: 5,
    //     order: 3,
    //   },
    // ];

    // const performanceChart =
    //   tokenType === "agETH"
    //     ? AGETH_PERFORMANCE_CHART
    //     : tokenType === "hgETH"
    //     ? HGETH_PERFORMANCE_CHART
    //     : [];

    // if (!performanceChart || performanceChart.length === 0) {
    //   return {
    //     labels: [],
    //     datasets: baseDatasets.map((dataset) => ({
    //       ...dataset,
    //       data: [],
    //     })),
    //     hideLegend: false,
    //     showExchangeRate: false,
    //     sixMonthApr: null,
    //     aprLabel: "6 months trailing APR",
    //   };
    // }

    // // Sort the data by date in ascending order
    // const sortedData = [...performanceChart].sort((a, b) => {
    //   return (
    //     new Date(a.end_week_date).getTime() -
    //     new Date(b.end_week_date).getTime()
    //   );
    // });

    // // Calculate 6-month trailing APR for agETH/hgETH (last data point)
    // const lastDataPoint = sortedData[sortedData.length - 1];
    // const sixMonthTrailingApr = lastDataPoint
    //   ? parseFloat(lastDataPoint.total_apr)
    //   : null;

    // return {
    //   labels: sortedData.map((item) => item.end_week_date),
    //   datasets: [
    //     {
    //       ...baseDatasets[0],
    //       data: sortedData.map((item) => parseFloat(item.total_apr)),
    //     },
    //     {
    //       ...baseDatasets[1],
    //       data: sortedData.map((item) => parseFloat(item.crystallised_apr)),
    //     },
    //     {
    //       ...baseDatasets[2],
    //       data: sortedData.map((item) => parseFloat(item.uncrystallised_apr)),
    //     },
    //   ],
    //   hideLegend: false,
    //   showExchangeRate: false,
    //   sixMonthApr: sixMonthTrailingApr,
    //   aprLabel: "6 months trailing APR",
    // };
  }, [tokenType, processedData, currentApr]);

  return {
    chartData,
    isLoading: isLoadingApr,
    metadata: { selectedTimeframeApr: chartData?.selectedTimeframeApr ?? null },
  };
};

export default useVaultPerformance;
